/**
 * Tunable defaults for the docked canvas's auto-zoom-to-active-feature-group
 * behavior (see useZoomToActiveFeatureGroup.ts). This replaces the old
 * per-part SCSS transform tables (scss/bv/responsive-canvas-*.scss) — those
 * only worked while the canvas was an in-flow DOM node the wizard's CSS could
 * target directly (`.selection-main__image--<partId> canvas`). The docked
 * canvas is a persistent, always-mounted overlay (VisualizerDockShell) that no
 * longer lives inside `.selection-main__image`, so the equivalent "zoom to
 * this part" behavior now has to be driven here, in JS, against the live
 * react-zoom-pan-pinch transform.
 *
 * Everything below is meant to be re-tuned by eye against the live app — the
 * numbers are reasonable starting points, not pixel-verified values:
 *  - the default scale mirrors the old lib/pinch-zoom `tapZoomFactor: 1.75`.
 *  - the idle pans are ported from each scss file's "DEFAULT POSITION FOR
 *    VISUALIZER" translate3d(x%, y%, 0) comment.
 */

import type { FeatureZoomOverride } from "./featureGroupZoomOverrides/featureZoomOverrideTypes";
import { getLaundryRoomFeatureZoomOverride } from "./featureGroupZoomOverrides/laundryRoomFeatureZoomOverrides";
import { getKvSceneFeatureZoomOverride } from "./featureGroupZoomOverrides/kvSceneFeatureZoomOverrides";
import { getBvLookFeatureZoomOverride } from "./featureGroupZoomOverrides/bvLookFeatureZoomOverrides";
import { getKitchenFeatureZoomOverride } from "./featureGroupZoomOverrides/kitchenFeatureZoomOverrides";
import { getBathroomFeatureZoomOverride } from "./featureGroupZoomOverrides/bathroomFeatureZoomOverrides";
import { getBvSceneFeatureZoomOverride } from "./featureGroupZoomOverrides/bvSceneFeatureZoomOverrides";
import { fireplaceFeatureZoomOverrides } from "./featureGroupZoomOverrides/fireplaceFeatureZoomOverrides";

// react-zoom-pan-pinch's setTransform(x, y, scale, animationTime, animationType) only accepts a
// named easing (no custom cubic-bezier support). The old canvas transition was
// `transition: all 1.3s cubic-bezier(0.4, 0.01, 0.51, 1)` — "easeInOutCubic" is the closest built-in
// shape (slow in/out over the whole move), and 1300ms matches the old duration exactly.
export const FEATURE_ZOOM_ANIMATION_MS = 1300;
export const FEATURE_ZOOM_EASING = "easeInOutCubic";

// Fallback zoom scale for any visualizerType/partId not listed below (mirrors the old
// lib/pinch-zoom `options.tapZoomFactor`).
export const DEFAULT_FEATURE_ZOOM_SCALE = 1.5;

// Per-part overrides, for the handful of feature groups that need a different zoom and/or a
// nudge off canvas-center — the JS analog of the old SCSS per-part rules (those were often keyed
// by room layout too, e.g. responsive-canvas-bvLook.scss's `&--layout18 { ... }` /
// `&--layout19 { ... }` blocks).
//
// This is implemented as a switch/case lookup (getFeatureZoomOverride below) rather than a plain
// object, one file + one switch per visualizerType (see ./featureGroupZoomOverrides/), so that
// parts sharing the exact same override can fall through shared `case` labels into a single
// `return` instead of repeating the same object literal — e.g.
// `case "valve_trim": case "diverter_trim": return {...}` in bvLookFeatureZoomOverrides.ts.
//
// scale, offsetX, and offsetY are each independent, per-field overrides — set only the ones a part
// actually needs tuned; anything you leave out keeps its own separate fallback:
//  - scale: falls back to DEFAULT_FEATURE_ZOOM_SCALE if not set (see getFeatureZoomScale).
//  - offsetX / offsetY: EACH AXIS falls back independently to that axis of the feature group's own
//    bounding-box center (getFeatureGroupCommonCoords + getCoordsCenter) if not set — e.g. an entry
//    with only `offsetX` still gets its Y position from the bounding-box center, and an entry with
//    only `scale` gets BOTH X and Y from the bounding-box center (unchanged from having no entry at
//    all). When an axis IS set, it's a 0-100 PERCENT ALONG THAT AXIS'S OWN PANNABLE RANGE — 0 is
//    that axis's start edge (fully left for offsetX, fully top for offsetY), 50 is dead center,
//    100 is the end edge (fully right / fully bottom). See percentToFocalCoord in
//    ../canvasTransformMath.ts for exactly how this resolves to a position. Because it's a percent
//    of the REAL pan range for the current viewport/scale — not a raw pixel count, and not a
//    percent of the canvas's own size — the same value reads the same way on every screen/dock-box
//    size: 0 and 100 always land exactly on the real edges and can never saturate partway there.
//    Setting BOTH offsetX and offsetY lets useZoomToActiveFeatureGroup.ts skip the
//    getFeatureGroupCommonCoords bounding-box walk entirely for that part/layout (the expensive part
//    of this whole feature), since neither axis needs it anymore.
//
// offsetX/offsetY use the exact same 0-100-percent-of-pan-range unit as
// IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE below, so a value copied from one to the other means the same
// thing. If a part should land exactly where the idle state rests, use `matchIdle: true` instead
// (see below) rather than copying that visualizerType's idle x/y by hand.
//
// Shape returned by getFeatureZoomOverride(visualizerType, partId), or `undefined` if neither the
// visualizerType nor the partId has a case — see FeatureZoomOverride in
// ./featureGroupZoomOverrides/featureZoomOverrideTypes.ts:
//   {
//     scale?: number, offsetX?: number, offsetY?: number,   // used when no layout-specific entry matches
//     matchIdle?: boolean,   // if true, ignore scale/offsetX/offsetY entirely and frame this
//                             // feature exactly like the idle/no-active-feature resting position
//                             // (IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE + minScale) instead of
//                             // zooming to its own bounding-box center — for a part whose own
//                             // coords don't produce a useful frame (e.g. a wall).
//     byLayoutType?: {
//       [layout.type]: { scale?: number, offsetX?: number, offsetY?: number, matchIdle?: boolean },   // wins over the fields above
//     },
//   }
//
// `layout.type` is the numeric room-layout id off the current layout singleton (getCurrentLayout().type)
// — the same value the old getCorrectedCenter() switched on (`switch (layout.type) { case 18: ... }`).
//
// Add new cases to the relevant visualizerType's file under ./featureGroupZoomOverrides/ as
// specific parts need tuning, e.g. in bvLookFeatureZoomOverrides.ts:
//   case "vanity_mirror":
//     return {
//       offsetY: 35,               // fallback for any layout not listed below (a bit above center)
//       byLayoutType: {
//         18: { offsetY: 20 },     // higher above center
//         19: { offsetY: 40 },     // barely above center
//       },
//     };
//   case "crown_molding":
//     return { scale: 2, offsetY: 15 };   // same scale/offset for every layout (well above center)
//
// If a new visualizerType needs overrides, add a new file under ./featureGroupZoomOverrides/
// (following the naming pattern `<visualizerType>FeatureZoomOverrides.ts`) and wire it into the
// switch in getFeatureZoomOverride below.
//
// The `scale` values in those files (2026-08-15) were bulk-ported from every
// scss/bv/responsive-canvas-*.scss file's per-part `scale(X)` value, as a mechanical starting point:
//  - a part with no `scale()` anywhere in its old rule (i.e. scale 1 — no extra zoom) has no case
//    at all, so it just falls through to the visualizerType default above.
//  - a part whose old scale varied by room layout and/or scene (bvLook/kvScene/LaundryRoom nest rules
//    under `&--layoutN`/`&--scene-N`) was collapsed to whichever non-1 scale value showed up most often
//    across those layouts/scenes — a real, if approximate, single number rather than a fully faithful
//    per-layout port. Use `byLayoutType` (see bvLookFeatureZoomOverrides.ts's
//    `valve_trim`/`diverter_trim`/`hand_shower_kit`/`shower_wall` cases) to split one back out if a
//    part needs different zoom per room layout.
//  - `bvLook`'s `walk_in_tub` is intentionally left as manually tuned (not the mechanically-ported
//    value) — don't overwrite it when re-running the port.
//  - Kitchen/Bathroom/bvScene cases are seeded from their scss files even though those types aren't
//    on the docked canvas yet (see FEATURE_ZOOM_SCALE_BY_VISUALIZER_TYPE above), so they're ready
//    if/when those types move over.

// Looks up the per-part override for a visualizerType — the switch/case analog of what used to be
// `FEATURE_ZOOM_OVERRIDES_BY_VISUALIZER_TYPE[visualizerType]?.[partId]`. Returns undefined when
// either the visualizerType or the partId has no case (falls back to the visualizerType default /
// bounding-box center, same as a missing object entry did before). Each visualizerType's own
// partId switch lives in its own file under ./featureGroupZoomOverrides/ (imported above).
//
// visualizerType is typed as `string` (not the narrower VisualizerType from src/interfaces/Global)
// because this switch also matches scene-flavored runtime values ("kvScene", "bvLook", "bvScene")
// that aren't part of that type.
function getFeatureZoomOverride(visualizerType: string, partId: string): FeatureZoomOverride | undefined {
  switch (visualizerType) {
    case "LaundryRoom":
      return getLaundryRoomFeatureZoomOverride(partId);
    case "kvScene":
      return getKvSceneFeatureZoomOverride(partId);
    case "bvLook":
      return getBvLookFeatureZoomOverride(partId);
    case "Kitchen":
      return getKitchenFeatureZoomOverride(partId);
    case "Bathroom":
      return getBathroomFeatureZoomOverride(partId);
    case "bvScene":
      return getBvSceneFeatureZoomOverride(partId);
    case "Fireplace":
    case "fireplace":
      return fireplaceFeatureZoomOverrides(partId);
    default:
      return undefined;
  }
}

interface IdlePanPercentEntry {
  x: number;
  y: number;
  byLayoutType?: Record<number, { x: number; y: number }>;
}

// Idle/no-active-feature resting pan, as a 0-100 percent along each axis's own pannable range —
// the SAME unit as offsetX/offsetY above (0 = start edge, 50 = dead center, 100 = end edge; see
// percentToFocalCoord in ../canvasTransformMath.ts). Applied at the current cover scale (see
// useZoomToActiveFeatureGroup's idle branch) whenever nothing is active or zoomable in the docked
// slot.
//
// 2026-08-22: this used to be "percent of the canvas's own content size, offset from center"
// (ported from each scss file's "DEFAULT POSITION FOR VISUALIZER" translate3d(x%, y%, 0) comment,
// with 0 meaning center). That unit could still saturate computeFocalTransform's clamp before
// reaching its nominal value whenever the docked box's aspect ratio drifted from the canvas's own
// aspect (a wide box was fine, a narrow/tall one could pin an axis to an edge instead of panning
// as far as requested — this is what forced bvLook's layout 18/19 byLayoutType entries below to
// exist in the first place). Percent-of-pan-range doesn't have that problem: 0 and 100 always land
// exactly on the real edges on any screen/dock-box size, so a value here means the same thing
// everywhere. The values below were mechanically re-expressed from the old unit (old value + 50,
// since the old unit's center was 0 and this unit's center is 50) as a starting point — re-tune by
// eye against the live docked box, same as every other value in this file.
export const IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE: Record<string, IdlePanPercentEntry> = {
  Bathroom: { x: 10, y: 61 },
  Kitchen: { x: 25, y: 50 },
  bvScene: { x: 34.5, y: 55 },
  LaundryRoom: { x: 47, y: 55 },
  kvScene: { x: 47, y: 55 },
  bvLook: {
    x: 50,
    y: 60,
    byLayoutType: {
      // These two used to exist to dodge the old unit's clamp-saturation problem in a
      // narrow/portrait docked box (see the 2026-08-22 note above) — that's no longer a concern
      // under percent-of-pan-range, but they're kept (converted to the new unit) since they may
      // still represent an intentionally different resting composition for these layouts. Re-tune
      // by eye against the live docked box if that's no longer the case.
      18: { x: 50, y: 50 }, // center
      19: { x: 40, y: 50 },
    },
  },
  Fireplace: { x: 17.75, y: 58 },
};

// A layout-specific entry always wins over the part's own base fields, which in turn win over
// the visualizerType-wide default — so a part can set a fallback plus per-layout exceptions, or
// skip byLayoutType entirely if every layout should behave the same.
function getLayoutAwareOverride(visualizerType: string, partId: string, layoutType: number | undefined) {
  const override = getFeatureZoomOverride(visualizerType, partId);
  const layoutOverride = layoutType !== undefined ? override?.byLayoutType?.[layoutType] : undefined;
  return { override, layoutOverride };
}

export function getFeatureZoomScale(visualizerType: string, partId: string, layoutType: number | undefined): number {
  const { override, layoutOverride } = getLayoutAwareOverride(visualizerType, partId, layoutType);
  const scale = layoutOverride?.scale ?? override?.scale;
  if (typeof scale === "number") {
    return scale;
  }
  return DEFAULT_FEATURE_ZOOM_SCALE;
}

// Returns each axis RAW (a 0-100 percent along that axis's pan range, see the shape comment above)
// — null when neither the layout-specific nor the base entry set that axis, rather than defaulting
// to 50 — so the caller can tell "explicitly centered on canvas (50)" apart from "not configured,
// fall back to the feature group's own bounding-box center for this axis" (useZoomToActiveFeatureGroup.ts
// is what actually falls back, and what converts a non-null percent to a position via
// percentToFocalCoord).
export function getFeatureZoomOffset(
  visualizerType: string,
  partId: string,
  layoutType: number | undefined,
): { x: number | null; y: number | null } {
  const { override, layoutOverride } = getLayoutAwareOverride(visualizerType, partId, layoutType);
  const x = layoutOverride?.offsetX ?? override?.offsetX;
  const y = layoutOverride?.offsetY ?? override?.offsetY;
  return {
    x: typeof x === "number" ? x : null,
    y: typeof y === "number" ? y : null,
  };
}

export function getIdlePanPercent(visualizerType: string, layoutType: number | undefined): { x: number; y: number } {
  const entry = IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE[visualizerType];
  if (!entry) {
    return { x: 50, y: 50 }; // no entry for this visualizerType — rest dead center
  }
  const layoutOverride = layoutType !== undefined ? entry.byLayoutType?.[layoutType] : undefined;
  return {
    x: layoutOverride?.x ?? entry.x ?? 50,
    y: layoutOverride?.y ?? entry.y ?? 50,
  };
}

// True when this part (optionally narrowed by layoutType) should skip its own scale/offset
// framing and instead land exactly on the idle resting position — see matchIdle in the shape
// comment above.
export function getFeatureZoomMatchesIdle(visualizerType: string, partId: string, layoutType: number | undefined): boolean {
  const { override, layoutOverride } = getLayoutAwareOverride(visualizerType, partId, layoutType);
  return Boolean(layoutOverride?.matchIdle ?? override?.matchIdle);
}
